import { useState } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';

export default function SyncReviewModal({ items = [], slots = {}, app, writing, onConfirm, onDryRun, onClose }) {
    const [writeMode] = useState(app?.writeback_mode || 'sql_table');
    const [forceWrite, setForceWrite] = useState(false);
    const [selectedIds, setSelectedIds] = useState(() => new Set(items.map(i => i.id)));

    // Detect items in conflict state (source changed since pull)
    const conflictItems = items.filter(i => i.writeback_status === 'conflict');
    const pendingItems  = items.filter(i => i.writeback_status === 'pending');
    const displayItems  = forceWrite ? [...conflictItems, ...pendingItems] : pendingItems;

    // Collect all unique field names changed across all selected items
    const allChangedFields = [...new Set(
        items.filter(i => selectedIds.has(i.id))
             .flatMap(i => Object.keys(i.field_overrides || {}))
    )];
    const [includedFields, setIncludedFields] = useState(() => new Set(allChangedFields));

    const toggleId = (id) => setSelectedIds(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const toggleField = (f) => setIncludedFields(prev => {
        const next = new Set(prev);
        next.has(f) ? next.delete(f) : next.add(f);
        return next;
    });



    const selectedCount = selectedIds.size;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-slate-900 rounded-3xl border border-gray-150 dark:border-slate-800 shadow-[0_25px_80px_rgba(0,0,0,0.2)] w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-slate-800 flex-none">
                    <div>
                        <h2 className="text-base font-extrabold text-gray-900 dark:text-slate-100 flex items-center gap-2">
                            <Upload className="w-4 h-4 text-indigo-500" /> Review &amp; Configure Sync
                        </h2>
                        <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-0.5">
                            {selectedCount} of {items.length} item{items.length !== 1 ? 's' : ''} selected
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-all">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Conflict Warning Banner */}
                {conflictItems.length > 0 && (
                    <div className="flex-none px-6 py-3 bg-orange-50 dark:bg-orange-950/30 border-b border-orange-200 dark:border-orange-800/40">
                        <div className="flex items-start gap-3">
                            <span className="text-orange-500 text-base mt-0.5">⚠️</span>
                            <div className="flex-1">
                                <p className="text-xs font-extrabold text-orange-700 dark:text-orange-400">
                                    {conflictItems.length} item{conflictItems.length !== 1 ? 's' : ''} have source conflicts
                                </p>
                                <p className="text-[10px] text-orange-600/80 dark:text-orange-500/80 mt-0.5">
                                    The source database was updated after your last pull. These items were skipped to prevent overwriting newer data.
                                </p>
                            </div>
                            <button onClick={() => setForceWrite(v => !v)}
                                className={`flex-none px-3 py-1.5 rounded-xl text-[11px] font-bold border transition-all ${forceWrite ? 'border-orange-500 bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300' : 'border-orange-300 dark:border-orange-700 text-orange-600 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/30'}`}>
                                {forceWrite ? '✓ Force Write ON' : 'Force Write (override)'}
                            </button>
                        </div>
                    </div>
                )}


                <div className="flex-1 overflow-auto px-6 py-4 custom-scrollbar space-y-3">
                    {displayItems.map(item => {
                        const overrides = item.field_overrides || {};
                        const source    = item.source_row_data || {};
                        const title     = item.resolved?.[slots.title] || item.id;
                        const selected  = selectedIds.has(item.id);
                        const changedFields = Object.entries(overrides).filter(([f]) => includedFields.has(f));

                        const isConflict = item.writeback_status === 'conflict';

                        return (
                            <div key={item.id} className={`border rounded-2xl overflow-hidden transition-all ${selected ? (isConflict ? 'border-orange-300 dark:border-orange-700/60' : 'border-indigo-200 dark:border-indigo-800/60') : 'border-gray-100 dark:border-slate-800 opacity-50'}`}>
                                <div className="px-4 py-2.5 bg-gray-50/60 dark:bg-slate-800/40 flex items-center gap-3">
                                    <input type="checkbox" checked={selected} onChange={() => toggleId(item.id)}
                                        className="w-3.5 h-3.5 rounded accent-indigo-600 cursor-pointer" />
                                    <span className="text-xs font-extrabold text-gray-700 dark:text-slate-200 truncate flex-1">{String(title)}</span>
                                    {isConflict && <span className="text-[10px] font-bold text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/30 border border-orange-300 dark:border-orange-700 px-2 py-0.5 rounded-full">⚠ conflict</span>}
                                    <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/40 px-2 py-0.5 rounded-full">
                                        {changedFields.length} field{changedFields.length !== 1 ? 's' : ''}
                                    </span>
                                </div>
                                {selected && changedFields.length > 0 && (
                                    <div className="divide-y divide-gray-50 dark:divide-slate-800/60">
                                        {changedFields.map(([field, newVal]) => {
                                            const oldVal = source[field];
                                            return (
                                                <div key={field} className="grid grid-cols-[140px_1fr_1fr] px-4 py-2 text-xs gap-3 items-center">
                                                    <span className="font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wide text-[10px] truncate">{field}</span>
                                                    <span className="text-red-500 dark:text-red-400 bg-red-50/60 dark:bg-red-955/20 px-2 py-1 rounded-lg truncate line-through opacity-70">
                                                        {oldVal !== undefined && oldVal !== null ? String(oldVal) : '—'}
                                                    </span>
                                                    <span className="text-emerald-600 dark:text-emerald-400 bg-emerald-50/60 dark:bg-emerald-955/20 px-2 py-1 rounded-lg truncate font-semibold">
                                                        {newVal !== undefined && newVal !== null ? String(newVal) : '—'}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-100 dark:border-slate-800 flex-none bg-gray-50/40 dark:bg-slate-900/60">
                    <button onClick={() => onDryRun({ writeMode, itemIds: [...selectedIds], fields: [...includedFields] })} disabled={writing || selectedCount === 0}
                        className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-xs font-bold text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-all disabled:opacity-50">
                        🔍 Dry Run Preview
                    </button>
                    <div className="flex-1" />
                    <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-xs font-bold text-gray-500 dark:text-slate-405 hover:bg-gray-50 dark:hover:bg-slate-800 transition-all">
                        Cancel
                    </button>
                    <button onClick={() => onConfirm({ writeMode, itemIds: [...selectedIds], fields: [...includedFields], forceWrite })}
                        disabled={writing || selectedCount === 0}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-extrabold shadow-md transition-all hover:scale-102 active:scale-98 disabled:opacity-50 disabled:pointer-events-none text-white ${
                            forceWrite
                                ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-orange-500/15'
                                : 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 shadow-indigo-500/15'
                        }`}>
                        {writing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        {forceWrite ? 'Force Sync ' : 'Sync '}{selectedCount} Item{selectedCount !== 1 ? 's' : ''}
                    </button>
                </div>
            </div>
        </div>
    );
}
